import { START_PAGE } from "@/constants/pagination";
import { FCC } from "@/types/common";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

export type TAdminUserFilterCache = {
  page: number;
};

const STORAGE_KEY = "adminUserFilterCache";

const DEFAULT_FILTER: TAdminUserFilterCache = {
  page: START_PAGE,
};

const loadFromStorage = (): TAdminUserFilterCache => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_FILTER;
    return { ...DEFAULT_FILTER, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_FILTER;
  }
};

type TAdminUserFilterCacheContext = {
  filterCache: TAdminUserFilterCache;
  setFilterCache: (value: TAdminUserFilterCache) => void;
};

const AdminUserFilterCacheContext = createContext<TAdminUserFilterCacheContext>({
  filterCache: DEFAULT_FILTER,
  setFilterCache: () => {},
});

export const AdminUserListFilterCacheProvider: FCC = ({ children }) => {
  const [filterCache, setFilterCacheState] = useState<TAdminUserFilterCache>(loadFromStorage);

  const setFilterCache = useCallback((value: TAdminUserFilterCache) => {
    setFilterCacheState(value);
    const { ...toStore } = value;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  }, []);

  const value = useMemo(() => ({ filterCache, setFilterCache }), [filterCache, setFilterCache]);

  return <AdminUserFilterCacheContext.Provider value={value}>{children}</AdminUserFilterCacheContext.Provider>;
};

export const useAdminUserFilterCache = () => useContext(AdminUserFilterCacheContext);
